import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTheme } from "../../context/ThemeContext";
import TransliteratedInput from "../common/TransliteratedInput";
import AutoSave from "../common/AutoSave";

const PatientInfoForm = (props) => {
  const { theme, isDarkMode } = useTheme();
  const { initialValues, onSubmit, onBack, isFirstStep, isLastStep, onAutoSave } = props;

  let patientInfo = null;
  if (initialValues) {
    patientInfo = initialValues.patientInfo?.[0] || {};
  }

  const formInitialValues = {
    PATIENT_NAME: patientInfo?.PATIENT_NAME || "",
    PATIENT_AGE: patientInfo?.PATIENT_AGE || "",
    PATIENT_ADDRESS: patientInfo?.PATIENT_ADDRESS || "",
    PATIENT_PHONE: patientInfo?.PATIENT_PHONE || "",
    PATIENT_OCCUPATION: patientInfo?.PATIENT_OCCUPATION || "",
    EMAILID: patientInfo?.EMAILID || "",
    PATIENT_GENDER: patientInfo?.PATIENT_GENDER || "",
    PATIENT_DOB: patientInfo?.PATIENT_DOB ? patientInfo.PATIENT_DOB.substring(0, 10) : "",
    PATIENT_BLOODGRP: patientInfo?.PATIENT_BLOODGRP || "",
    PATIENT_MOBILE_NUM: patientInfo?.PATIENT_MOBILE_NUM || "",
  };

  const validationSchema = Yup.object().shape({
    PATIENT_NAME: Yup.string().required("Required"),
    PATIENT_AGE: Yup.number().required("Required").positive("Invalid age").integer("Invalid age"),
    PATIENT_PHONE: Yup.string().required("Required"),
    EMAILID: Yup.string().email("Invalid email"),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        onSubmit(values);
        actions.setSubmitting(false);
      }}
      enableReinitialize={true}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="space-y-6 md:space-y-8 w-full">
          <AutoSave onAutoSave={(values) => (onAutoSave || onSubmit)?.(values, { isAutoSave: true })} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-2">
              <label
                htmlFor="PATIENT_NAME"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <Field name="PATIENT_NAME">
                {({ field, form }) => (
                  <TransliteratedInput
                    {...field}
                    onChangeText={(text) => form.setFieldValue(field.name, text)}
                    className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_NAME && errors.PATIENT_NAME
                      ? 'border-red-500 focus:ring-red-500'
                      : 'focus:ring-opacity-50'
                      }`}
                    style={{
                      backgroundColor: theme.inputBg,
                      borderColor: touched.PATIENT_NAME && errors.PATIENT_NAME ? '#d63031' : theme.border,
                      color: theme.text,
                    }}
                    placeholder="Enter patient name"
                  />
                )}
              </Field>
              <ErrorMessage name="PATIENT_NAME" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_AGE"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Age <span className="text-red-500">*</span>
              </label>
              <Field
                name="PATIENT_AGE"
                type="number"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_AGE && errors.PATIENT_AGE
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.PATIENT_AGE && errors.PATIENT_AGE ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="PATIENT_AGE" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_PHONE"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Phone <span className="text-red-500">*</span>
              </label>
              <Field
                name="PATIENT_PHONE"
                type="text"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_PHONE && errors.PATIENT_PHONE
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.PATIENT_PHONE && errors.PATIENT_PHONE ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="PATIENT_PHONE" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="EMAILID"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Email
              </label>
              <Field
                name="EMAILID"
                type="email"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.EMAILID && errors.EMAILID
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.EMAILID && errors.EMAILID ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="EMAILID" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_DOB"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Date of Birth
              </label>
              <Field
                name="PATIENT_DOB"
                type="date"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_DOB && errors.PATIENT_DOB
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.PATIENT_DOB && errors.PATIENT_DOB ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="PATIENT_DOB" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_OCCUPATION"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Occupation
              </label>
              <Field name="PATIENT_OCCUPATION">
                {({ field, form }) => (
                  <TransliteratedInput
                    {...field}
                    onChangeText={(text) => form.setFieldValue(field.name, text)}
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      backgroundColor: theme.inputBg,
                      borderColor: theme.border,
                      color: theme.text,
                    }}
                    placeholder="Enter occupation"
                  />
                )}
              </Field>
              <ErrorMessage name="PATIENT_OCCUPATION" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div className="space-y-2 md:col-span-2">
            <label
              htmlFor="PATIENT_ADDRESS"
              className="block text-sm font-medium transition-colors"
              style={{ color: theme.text }}
            >
              Address
            </label>
            <Field name="PATIENT_ADDRESS">
              {({ field, form }) => (
                <TransliteratedInput
                  {...field}
                  onChangeText={(text) => form.setFieldValue(field.name, text)}
                  className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                  as="textarea"
                  rows={3}
                  placeholder="Enter address"
                />
              )}
            </Field>
            <ErrorMessage name="PATIENT_ADDRESS" component="div" className="text-red-500 text-sm mt-1" />
          </div>

          <div
            className="flex justify-between items-center pt-2 mt-4 border-t md:col-span-2 sticky bottom-0 z-[100] py-3 -mx-4 md:-mx-10 lg:-mx-12 px-4 md:px-10 lg:px-12"
            style={{
              borderColor: theme.border,
              backgroundColor: theme.card,
              boxShadow: '0 -4px 10px rgba(0,0,0,0.05)'
            }}
          >
            {!isFirstStep && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-1.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 active:scale-95 text-xs md:text-sm"
                style={{
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: theme.text,
                  border: `1px solid ${theme.border}`
                }}
              >
                Back
              </button>
            )}
            <div className={isFirstStep ? "ml-auto" : ""}>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 rounded-full font-bold text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base shadow-lg animate-flow"
                style={{
                  backgroundImage: `linear-gradient(to right, ${theme.brownTwo}, ${theme.secondary}, ${theme.primary}, ${theme.brownTwo})`,
                }}
              >
                {isLastStep ? "Submit" : "Next"}
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PatientInfoForm;
